/* istanbul ignore file */
/* eslint-disable react/no-danger */
import React from 'react';
import { Typography, ListItemText, Tooltip, Box, Divider, ListItemButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sanitizeHtml } from '../../../../utilities/sanitize-html';
import { RankedSearchModel } from '../../../../api-service/clients/auto-generated-clients';
import { CreateCitizenButton } from './create-citizen-button';
import { CreateCompanyButton } from './create-company-button';
import { generateRelatedEntityUrl } from '../../../citizen/common/journal/goto-resource-helper';
import { useRowActions } from '../../../search/contextProviders/row-action-provider';
import { getDocumentTypeDescriptor } from './utils/document-type-descriptor';

const listItemStyle = makeStyles()(() =>
  ({
    root: {
      margin: 0,
    },
  })
);

const textWrapperStyle = makeStyles()(() =>
  ({
    root: {
      margin: 0,
      '& em': {
        fontWeight: 500,
        color: '#999',
        fontStyle: 'normal',
        paddingLeft: 5,
        '&:first-child': {
          paddingLeft: 0,
        },
      },
      '& b': {
        fontWeight: 600,
      },
    },
  })
);

const descriptionStyle = makeStyles()(() =>
  ({
    root: {
      maxWidth: '800px',
      padding: 0,
      margin: 0,
    },
  })
);

const titleStyle = makeStyles()(() =>
  ({
    root: {
      maxWidth: '800px',
      padding: '1px 0px',
      margin: 0,
      lineHeight: 1,
    },
  })
);

const docTypeStyle = makeStyles()(() =>
  ({
    root: {
      padding: 0,
      margin: 0,
    },
    secondary: {
      color: '#4895EF',
    },
  })
);

export interface IRankedSearchResultItemProps extends RankedSearchModel {
  autoFocus?: boolean;
}
export function RankedSearchResultItem(props: IRankedSearchResultItemProps) {
  const { title, description, documentType, autoFocus, documentId } = props;
  const rowActions = useRowActions();

  function handleTitleClick() {
    const rowAction = rowActions && documentType !== undefined && rowActions.find(x => x.resultType.toLowerCase() === documentType.toLowerCase());
    if (rowAction && rowAction.url) {
      if (documentType?.toLowerCase() === 'cases') {
        const url = generateRelatedEntityUrl({ entityId: documentId || '', entityName: 'si_case' });
        window.open(url);
      } else {
        const url = rowAction.url.replace('{record.guid}', documentId || '').replace('{ticks}', new Date().getTime().toString());
        if (rowAction.openNewWindow) {
          window.open(url, rowAction.resultType + documentId);
        } else {
          window.location.assign(url);
        }
      }
    }
  }

  const { classes: listItemClasses } = listItemStyle();
  const { classes: textWrapper } = textWrapperStyle();
  const { classes: descriptionClasses } = descriptionStyle();
  const { classes: titleClasses } = titleStyle();
  const { classes: docTypeClasses } = docTypeStyle();
  const documentTypeDescriptor = getDocumentTypeDescriptor(documentType || 'citizen');
  const isCimCitizen = documentType?.toLowerCase() === 'cimcitizen';
  const isDfdgCitizen = documentType?.toLowerCase() === 'dfdgcitizen';
  const isCvrCompany = documentType?.toLowerCase() === 'cvrcompany';
  const isCmCompany = documentType?.toLowerCase() === 'cmcompany';

  return (
    <Box mt={1}>
      <ListItemButton
        autoFocus={autoFocus}
        classes={listItemClasses}
        style={{ borderLeft: `solid 7px ${documentTypeDescriptor?.color}`, paddingTop: '2px' }}
        alignItems="flex-start"
        onClick={() => handleTitleClick()}
      >
        <ListItemText classes={textWrapper}>
          <ListItemText classes={docTypeClasses} secondary={documentTypeDescriptor?.type} />
          <Tooltip title={title || ''} enterDelay={500}>
            <Typography noWrap classes={titleClasses} variant="h6">
              {title}
            </Typography>
          </Tooltip>
          <Tooltip title={<span dangerouslySetInnerHTML={{ __html: sanitizeHtml(description || '') }} />} enterDelay={500}>
            <Typography noWrap classes={descriptionClasses} variant="body1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(description || '') }} />
          </Tooltip>
        </ListItemText>
        {isDfdgCitizen && <CreateCitizenButton cpr={documentId} />}
        {isCvrCompany && <CreateCompanyButton productionUnitIdentifier={documentId} />}
        {isCimCitizen && <FontAwesomeIcon icon={faExclamationTriangle} />}
        {isCmCompany && <FontAwesomeIcon icon={faExclamationTriangle} />}
      </ListItemButton>
      <Divider />
    </Box>
  );
}
